<script>
import { required, maxLength } from "@vuelidate/validators";
import { mapActions, mapGetters } from "vuex";
import { CREATE_SUPPORT_BUG, UPLOAD_BUG_IMAGE } from "@/store/actions.type";
import useVuelidate from "@vuelidate/core";
import { Cropper } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";

export default {
  name: "BugReport",
  components: { Cropper },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      files: [],
      bug: {
        description: null,
        location: null,
        image: null,
        image_filename: null,
      },
      showModal: false,
      realImage: true,
      showError: false,
      temporaryProfilePicture: null,
      imageSize: null,
      sizeError: false,
      img: null,
      image: null,
      bugLocationOptions: [
        { label: "Logging in", value: "Logging_In" },
        { label: "Viewing my PenPic", value: "Viewing_PenPic" },
        { label: "Editing my personal details", value: "Editing_Personal" },
        { label: "Editing my summary", value: "Editing_Summary" },
        { label: "Editing my experience", value: "Editing_Experience" },
        { label: "Editing my additional details", value: "Editing_Additional" },
        { label: "Logging out", value: "Logging_Out" },
      ],
      bugLocationOptionsManager: [
        {
          label: "Searching for a candidate (Manager access only)",
          value: "Searching_admin",
        },
        {
          label: "Viewing a candidate's PenPic (Manager access only)",
          value: "Viewing_PenPic_admin",
        },
        {
          label: "Using the insights page (Manager access only)",
          value: "Using_Insights_admin",
        },
      ],
      bugLocationOptionsOther: [
        { label: "Other (Please Specify Below)", value: "Other" },
      ],
      isBugUploading: false,
      fileSizeError: false,
    };
  },
  computed: {
    ...mapGetters({
      isAuthenticated: "isAuthenticated",
      checkRole: "userHasRole",
      checkPerms: "userHasPerm",
    }),
    isAdmin() {
      if (this.isAuthenticated) return this.checkRole("Super Administrator");
    },
  },
  validations: {
    bug: {
      description: {
        required,
        maxLength: maxLength(500),
      },
      location: {
        required,
      },
    },
  },
  watch: {
    files(newFiles) {
      if (newFiles && newFiles.length > 0) {
        this.changeScreenshot(newFiles);
      }
    },
  },
  methods: {
    ...mapActions({
      createSupportBug: CREATE_SUPPORT_BUG,
      uploadBugImage: UPLOAD_BUG_IMAGE,
    }),
    change({ coordinates, canvas }) {
      // this.image = canvas.toDataURL("image/jpeg", 0.5);
      this.temporaryProfilePicture = canvas.toDataURL("image/jpeg", 0.5);
    },
    saveNewPhoto() {
      try {
        this.image = this.temporaryProfilePicture;
        if (this.image.length > 5000000) {
          this.sizeError = true;
          throw new Error("File is too big");
        }
        this.showError = false;
        this.bug.image = this.image;
        this.showModal = false;
      } catch (err) {
        this.realImage = false;
      }
    },
    selectFile(e) {
      this.img = "";

      const files = e.target.files;
      if (!files || !files.length) return;

      // Convert to dataURL and pass to the cropper component
      const file = files[0];
      this.bug.image_filename = `${new Date().toISOString()}-${file.name}`;
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Update the picture source of the `img` prop
        this.img = String(reader.result);
      };
    },
    async submit() {
      this.v$.$touch();
      if (!this.v$.$invalid) {
        try {
          this.isBugUploading = true;

          await this.createSupportBug(this.bug);
          if (this.bug.image_filename) await this.uploadBugImage(this.bug);
          /** clear out all values in the bug */
          Object.keys(this.bug).forEach((key) => (this.bug[key] = null));
          this.v$.$reset();
          this.isBugUploading = false;
          this.image = null;
          this.temporaryProfilePicture = null;
          this.img = null;
          this.bug = {
            description: null,
            location: null,
            image: null,
            image_filename: null,
          };
          this.$refs.modal.show();
        } catch (error) {
          this.isBugUploading = false;
          throw new Error(error);
        }
      }
    },

    goToLeaveFeedback() {
      this.$router.push("/feedback");
    },
    hideModal() {
      this.showModal = false;
    },
  },
};
</script>

<template>
  <cv-grid class="main">
    <div class="detail-tile-container">
      <div class="detail-description-container">
        <p class="detail-title"><strong> Found a bug? Let us know </strong></p>
        <p class="detail-description">
          Thank you for helping us to improve IBM SkillsMatch! Please give a
          detailed description of the bug, so that we are able to provide the
          relevant support. All responses are anonymous, therefore,
          <strong>
            if you would like to be contacted, please include an email or slack
            username.
          </strong>
        </p>
      </div>
      <cv-loading :active="isBugUploading" :overlay="true" />
      <cv-modal ref="modal">
        <template #title>Bug report sent</template>
        <template #content>
          <p>
            Thank you for submitting your bug report. Your report has been
            submitted and will be addressed as soon as possible.
          </p>
        </template>
      </cv-modal>

      <div style="display: flex; flex-direction: row">
        <div class="detail-input-container">
          <cv-interactive-tooltip direction="top">
            <template #label>Bug occured when: </template>
            <template #content>
              <p>
                Please select an option from below which best describes the bug.
              </p>
            </template>
          </cv-interactive-tooltip>
          <br />
          <cv-radio-group :vertical="true" legend-text=" ">
            <cv-radio-button
              v-for="location in bugLocationOptions"
              :key="location.value"
              v-model="bug.location"
              name="group-1"
              :label="location.label"
              :value="location.value"
            />
          </cv-radio-group>
          <cv-radio-group
            v-if="isAdmin"
            legend-text=" "
            :vertical="true"
            class="additional-options"
          >
            <cv-radio-button
              v-for="location in bugLocationOptionsManager"
              v-show="checkPerms('ABF')"
              :key="location.value"
              v-model="bug.location"
              name="group-1"
              :label="location.label"
              :value="location.value"
            />
          </cv-radio-group>
          <cv-radio-group
            :vertical="true"
            legend-text=" "
            class="additional-options"
          >
            <cv-radio-button
              v-for="location in bugLocationOptionsOther"
              :key="location.value"
              v-model="bug.location"
              name="group-1"
              :label="location.label"
              :value="location.value"
            />
          </cv-radio-group>
          <div v-if="v$.bug.location.$error" class="error-message">
            An option must be selected
          </div>
        </div>
        <img
          v-if="image"
          style="max-width: 40vw; max-height: 50vh; object-fit: contain"
          :src="image"
        />
      </div>
      <div>
        <cv-interactive-tooltip direction="bottom">
          <template #label>Additional Details</template>
          <template #content>
            <p>
              Any information that may help us to fix this bug as quickly as
              possible.
            </p>
          </template>
        </cv-interactive-tooltip>
        <cv-text-area
          v-model="bug.description"
          placeholder="e.g. What you were doing when the bug occured"
          invalid-message
        >
        </cv-text-area>

        <div v-if="v$.bug.description.$error" class="error-message">
          Additional Details field is needed and can not exceed 500 characters
        </div>
        <br />
        <span>
          Characters Remaining:
          {{ bug.description ? 500 - bug.description.length : 500 }}
        </span>
      </div>
      <cv-interactive-tooltip direction="bottom">
        <template #label>Screenshot upload: </template>
        <template #content>
          <p>Please provide us with a screenshot of the error that occured.</p>
        </template>
      </cv-interactive-tooltip>

      <cv-modal
        ref="modalview"
        class="picture-modal"
        :visible="showModal"
        :primary-button-disabled="!temporaryProfilePicture"
        @modal-hidden="hideModal"
        @primary-click="
          () => {
            saveNewPhoto();
          }
        "
      >
        <template #title>Upload and Crop</template>
        <template #label>
          Some browsers such as Safari require scrolling on the mouse or a
          two-finger scroll gesture on the trackpad to adjust the image size.
        </template>
        <template #content>
          <cropper style="max-height: 90%" :src="img" @change="change" />
          <div class="button-container">
            <input
              type="file"
              accept="image/jpg, image/jpeg, image/png, image/gif"
              @change="selectFile"
            />
          </div>
          <div v-if="!realImage">
            <p class="bad-pic-message">
              The image uploaded is broken, please try again.
            </p>
          </div>
          <cv-inline-notification
            v-if="sizeError"
            kind="error"
            title="Error"
            sub-title="Image size is too big"
            @close="closeImageSizeError"
          />
        </template>
        <template #primary-button>Save Image</template>
        <template #secondary-button>Cancel</template>
      </cv-modal>
      <cv-inline-notification
        v-if="showError"
        kind="error"
        title="Error"
        sub-title="An image needs to be uploaded and cropped"
        @close="closeImageError"
      />

      <!-- Button for upload and crop -->
      <cv-button
        kind="tertiary"
        class="upload-button"
        @click="
          () => {
            showModal = true;
          }
        "
      >
        Upload and Crop
      </cv-button>
    </div>

    <cv-button-set class="button-set">
      <cv-button kind="secondary" @click="goToLeaveFeedback"> Back </cv-button>
      <cv-button kind="primary" @click="submit"> Submit </cv-button>
    </cv-button-set>
  </cv-grid>
</template>

<style scoped>
.file-upload {
  align-items: center;
  align-content: center;
}
.main {
  margin-left: 44px;
}
.error-message {
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.34;
  letter-spacing: 0.32px;
  margin: 0.25rem 0 0;
  overflow: hidden;
  color: #ff8389;
}
.submit-button-container {
  display: flex;
  justify-content: flex-start;
}

span {
  float: right;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.34;
  letter-spacing: 0.32px;
  display: inline-flex;
  align-items: center;
  color: #c6c6c6;
}

.additional-options {
  margin-top: 0.5rem;
}

.button-set {
  max-width: 1103px;
  display: flex;
  justify-content: flex-end;
  gap: 2rem;
  margin-left: auto;
  margin-right: 15px;
  padding: 0 1rem;
}

@media (max-width: 47.75rem) {
  .button-set {
    display: block;
    margin: 0 auto;
    padding: 0 1rem;
  }

  .button-set > * {
    width: 33%;
    max-width: none;
  }
}
</style>
